import { useRef, useState } from "react";
import { Plus, Trash2, Upload } from "lucide-react";
import type { Asset } from "../types";
import type { GameObject, ObjectState } from "./types";
import { stateKey } from "./states";
type Props = {
  object: GameObject;
  assets: Asset[];
  projectId: string;
  locked: boolean;
  onPatch(values: Partial<GameObject>): void;
  /** Applies a change to the layer as it is now, after an upload finishes. */
  onPatchLatest(change: (o: GameObject) => Partial<GameObject>): void;
  onUpload(file: File): Promise<Asset | null>;
};
// The layer's own picture is the unnamed base state; named states swap it.
// The selected radio is the state the scene starts in and the canvas shows.
export function StatesEditor({
  object,
  assets,
  projectId,
  locked,
  onPatch,
  onPatchLatest,
  onUpload,
}: Props) {
  const file = useRef<HTMLInputElement>(null),
    [target, setTarget] = useState<string | null>(null),
    [busy, setBusy] = useState(false);
  const states = object.states,
    thumb = (id: string | null) =>
      id ? `/api/projects/${projectId}/assets/${id}/preview/128` : null;
  const update = (id: string, values: Partial<ObjectState>) =>
    onPatch({
      states: states.map((s) => (s.id === id ? { ...s, ...values } : s)),
    });
  const duplicateName = (s: ObjectState) =>
    states.some((o) => o.id !== s.id && stateKey(o.name) === stateKey(s.name));
  function add() {
    let n = states.length + 1;
    while (states.some((s) => stateKey(s.name) === `состояние ${n}`)) n++;
    const current = states.find((s) => s.id === object.state);
    onPatch({
      states: [
        ...states,
        {
          id: crypto.randomUUID(),
          name: `Состояние ${n}`,
          imageId: current?.imageId ?? object.imageId,
        },
      ],
    });
  }
  const row = (
    key: string | null,
    name: string,
    imageId: string | null,
    controls: React.ReactNode,
  ) => (
    <div className="state-row" key={key ?? "base"}>
      <input
        type="radio"
        name={`state-${object.id}`}
        aria-label={`Начальное состояние: ${name}`}
        title="Показывать при старте"
        checked={object.state === key}
        disabled={locked}
        onChange={() => onPatch({ state: key })}
      />
      <span className="state-thumb" aria-hidden="true">
        {thumb(imageId) && <img src={thumb(imageId)!} alt="" />}
      </span>
      <div className="state-fields">{controls}</div>
    </div>
  );
  const picker = (
    value: string | null,
    label: string,
    change: (id: string | null) => void,
    stateId: string | null,
  ) => (
    <div className="state-picker">
      <select
        aria-label={label}
        value={value || ""}
        disabled={locked}
        onChange={(e) => change(e.target.value || null)}
      >
        <option value="">
          {stateId === null ? "Из сущности / не выбрано" : "Нет картинки"}
        </option>
        {assets.map((a) => (
          <option key={a.id} value={a.id}>
            {a.name}
          </option>
        ))}
      </select>
      <button
        className="icon-button"
        aria-label={`Загрузить картинку: ${label}`}
        title="Загрузить PNG, JPEG, WebP или GIF"
        disabled={locked || busy}
        onClick={() => {
          setTarget(stateId ?? "");
          file.current?.click();
        }}
      >
        <Upload size={13} />
      </button>
    </div>
  );
  return (
    <div className="scene-states">
      <div className="logic-section-title">
        <span className="eyebrow">СОСТОЯНИЯ КАРТИНКИ</span>
        <button
          className="text-button"
          disabled={locked || states.length >= 20}
          onClick={add}
        >
          <Plus size={13} />
          Состояние
        </button>
      </div>
      {row(
        null,
        "Исходная",
        object.imageId,
        <>
          <span className="state-name">Исходная</span>
          {picker(object.imageId, "Изображение", (imageId) => onPatch({ imageId }), null)}
        </>,
      )}
      {states.map((s) =>
        row(
          s.id,
          s.name,
          s.imageId,
          <>
            <div className="state-picker">
              <input
                aria-label="Название состояния"
                aria-invalid={duplicateName(s) || !s.name.trim()}
                value={s.name}
                maxLength={60}
                disabled={locked}
                onChange={(e) => update(s.id, { name: e.target.value })}
              />
              <button
                className="icon-button"
                aria-label={`Удалить состояние ${s.name}`}
                disabled={locked}
                onClick={() =>
                  onPatch({
                    states: states.filter((o) => o.id !== s.id),
                    ...(object.state === s.id && { state: null }),
                  })
                }
              >
                <Trash2 size={13} />
              </button>
            </div>
            {picker(
              s.imageId,
              `Картинка состояния ${s.name}`,
              (imageId) => update(s.id, { imageId }),
              s.id,
            )}
          </>,
        ),
      )}
      {states.some((s) => duplicateName(s) || !s.name.trim()) && (
        <p className="small-text error-message" role="alert">
          Названия состояний должны быть разными и непустыми.
        </p>
      )}
      <p className="small-text muted">
        Правило «Сменить состояние» переключает картинку по названию; GIF
        начинает играть заново.
      </p>
      <input
        ref={file}
        type="file"
        hidden
        accept="image/png,image/jpeg,image/webp,image/gif"
        onChange={async (e) => {
          const picked = e.target.files?.[0];
          e.target.value = "";
          if (!picked || target === null) return;
          setBusy(true);
          try {
            const asset = await onUpload(picked);
            if (!asset) return;
            onPatchLatest((o) =>
              target === ""
                ? { imageId: asset.id }
                : {
                    states: o.states.map((s) =>
                      s.id === target ? { ...s, imageId: asset.id } : s,
                    ),
                  },
            );
          } finally {
            setBusy(false);
          }
        }}
      />
    </div>
  );
}
